import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ControlledInput, FormCard, FormGrid, IParams, Page, Section } from '../../../shared';
import { DownloadCard } from '../../components';
import { ISaveOperator } from '../../models';
import { useNameSchema } from '../../validators';
import { useOperator, useSaveOperator } from '../../hooks';

export const OperatorEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { id } = useParams<keyof IParams>() as IParams;
    const { data: operator, isFetching } = useOperator(id);

    const form = useForm<ISaveOperator>({
        resolver: yupResolver(useNameSchema()),
        mode: 'all',
    });
    const { mutateAsync: saveOperator, isPending: isPendingSave } = useSaveOperator();

    useEffect(() => {
        if (operator) {
            form.reset(operator);
        }
    }, [operator, form]);

    const onSubmit = useCallback(
        async (item: ISaveOperator) => {
            await saveOperator({ id, item });
            navigate('/admin/operators');
        },
        [id, navigate, saveOperator],
    );

    const actions = useMemo(
        () => [
            <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)} disabled={isFetching}>
                {t('save')}
            </Button>,
            operator ? <DownloadCard variant="outlined" name={operator.name} code={operator.code} /> : <></>,
            <Button onClick={() => navigate('/admin/operators')} color="secondary">
                {t('cancel')}
            </Button>,
        ],
        [form, onSubmit, isFetching, t, operator, navigate],
    );
    const reversedActions = useMemo(() => [...actions].reverse(), [actions]);

    return (
        <Page
            title={operator?.name || t('newOperator')}
            actions={reversedActions}
            onBack={() => navigate(-1)}
            loading={isFetching || isPendingSave}
        >
            <FormProvider {...form}>
                <FormCard handleSubmit={form.handleSubmit(onSubmit)} actions={actions}>
                    <Section title={t('data')}>
                        <FormGrid>
                            <ControlledInput name="name" label={t('name')} required={true} />
                            {id && <ControlledInput name="code" label={t('code')} disabled />}
                        </FormGrid>
                    </Section>
                </FormCard>
            </FormProvider>
        </Page>
    );
};
